import { useCallback } from "react";
import { useSafeQuery } from "@/hooks/use-safe-query";
import {
  RESTAURANT_CONFIG_SESSION_TTL_MS,
  restaurantConfigQueryKey,
} from "@/query-keys/restaurant.keys";
import { fetchRestaurantConfiguration } from "@/services/restaurant-config";
import type { RestaurantConfiguration } from "@/domain/restaurant/restaurant.types";

export type UseRestaurantConfigQueryArgs = {
  /** Platform slug from PublicTenantProvider — required for PLATFORM_SLUG public URL. */
  slug: string;
  /** Bootstrap restaurant id — cache key / response fallback only. */
  restaurantId: string;
  /** When true (CUSTOM_HOSTNAME), slug may be empty; host-only public path is used. */
  hostOnly?: boolean;
};

export function useRestaurantConfigQuery(args: UseRestaurantConfigQueryArgs) {
  const slug = args.slug.trim();
  const restaurantId = args.restaurantId.trim();
  const hostOnly = Boolean(args.hostOnly);
  const queryKey = restaurantConfigQueryKey(restaurantId);

  const fetcher = useCallback(
    (signal: AbortSignal) => fetchRestaurantConfiguration(slug, restaurantId, signal),
    [slug, restaurantId]
  );

  // Branding fields (logo_url, favicon_url, theme_color, primary_button_color,
  // secondary_button_color, facebook_url, instagram_url)
  // ride this same in-memory restaurant-config cache. Updates may remain cached until
  // RESTAURANT_CONFIG_SESSION_TTL_MS expires. Invalidation is deferred to a later PR.
  return useSafeQuery<RestaurantConfiguration>({
    queryKey,
    enabled: Boolean(restaurantId && (hostOnly || slug)),
    fetcher,
    cache: "memory",
    ttlMs: RESTAURANT_CONFIG_SESSION_TTL_MS,
  });
}
